'use client';

import { formatISO } from 'date-fns';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { LegacyBaseTrackingEvent, LegacyTrackingEvent } from '@//data/tracking/legacy-types';
import { useCreateLogStash } from '@//data/tracking/useCreateLogStash';
import { useAuth } from './AuthProvider';

// Extend Window interface for GTM
declare global {
  interface Window {
    dataLayer: any[];
    gtmLoadFailed?: boolean;
  }
}

// GTM Event Handler - Direct dataLayer push optimized for server-side enrichment
const pushToGTMDataLayer = (event: LegacyTrackingEvent): void => {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Create GTM event with the new structure
  const gtmEvent = {
    // GTM required field - using the event name
    event: event.event,

    // All event data wrapped in the data field
    data: event.data,
  };

  // Push to GTM dataLayer
  window.dataLayer.push({ data: null });
  window.dataLayer.push(gtmEvent);

  // Log in development mode
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: allow console logging in dev
    console.log('Legacy GTM Event (via dataLayer):', gtmEvent);
  }
};

// Safe storage access with fallback for private browsing mode
const safeStorageAccess = {
  getItem: (storage: Storage, key: string): string | null => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (storage: Storage, key: string, value: string): void => {
    try {
      storage.setItem(key, value);
    } catch {
      // Silently fail in private browsing mode
    }
  },
};

// Generate persistent ID that persists across browser sessions (maps to unAuthId)
const getPersistentId = (): string => {
  if (typeof window === 'undefined') return uuidv4();

  let persistentId = safeStorageAccess.getItem(localStorage, 'tracking_persistent_id');
  if (!persistentId) {
    persistentId = uuidv4();
    safeStorageAccess.setItem(localStorage, 'tracking_persistent_id', persistentId);
  }
  return persistentId;
};

// Generate session ID that persists during the browser session
const getSessionId = (): string => {
  if (typeof window === 'undefined') return uuidv4();

  let sessionId = safeStorageAccess.getItem(sessionStorage, 'tracking_session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    safeStorageAccess.setItem(sessionStorage, 'tracking_session_id', sessionId);
  }
  return sessionId;
};

interface LegacyTrackingContextProps {
  track: (event: {
    event: string;
    data?: any; // Allow event-specific data without base properties
  }) => void;
  flushEvents: () => void; // Keep for API compatibility (no-op)
  isLoading: boolean;
  error: Error | null;
  queueSize: number;
}

const LegacyTrackingContext = createContext<LegacyTrackingContextProps | undefined>(undefined);

interface LegacyTrackingProviderProps {
  children: React.ReactNode;
}

export const LegacyTrackingProvider = ({ children }: LegacyTrackingProviderProps) => {
  const { decodedToken, authResolved } = useAuth();
  const { addToQueue } = useCreateLogStash();

  // Store pending events only for auth resolution
  const pendingEventsRef = useRef<
    Array<{
      event: {
        event: string;
        data?: any;
      };
      timestamp: number;
    }>
  >([]);

  // Only track if GTM is enabled (automatically enabled in production)
  const isGTMEnabled =
    process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_GTM_ENABLED === 'true';

  // Simple flush function - no-op for GTM but keeps API compatibility
  const flushEvents = useCallback(() => {
    // GTM doesn't need manual flushing, events are sent immediately
  }, []);

  const createLegacyTrackingEvent = useCallback(
    (
      event: {
        event: string;
        data?: any;
      },
      timestamp?: number,
    ): LegacyTrackingEvent => {
      const userData: LegacyBaseTrackingEvent['data']['userData'] = {
        unAuthId: `unauth-${getPersistentId()}`,
        sessionId: getSessionId(),
        userType: 'N/A',
      };

      if (decodedToken) {
        userData.userId = decodedToken.sub;
        userData.login = true;
        userData.idp = decodedToken.idp;
        userData.userType = decodedToken['belongs-to-branch']?.length ? 'dealer' : 'private';
      }

      // Merge base data with event-specific data
      const baseData = {
        source: 'web' as const,
        url: typeof window !== 'undefined' ? window.location.href : '',
        eventTimestamp: formatISO(new Date(timestamp || Date.now())),
        userData,
      };

      const data = {
        ...baseData,
        ...event.data,
      };

      return {
        event: event.event || '',
        data,
      } as LegacyTrackingEvent;
    },
    [decodedToken],
  );

  // Process pending events once auth is resolved
  useEffect(() => {
    if (!authResolved || pendingEventsRef.current.length === 0) {
      return;
    }

    // Process all pending events with correct auth context
    const eventsToProcess = [...pendingEventsRef.current];
    pendingEventsRef.current = [];

    for (const { event, timestamp } of eventsToProcess) {
      const trackingEvent = createLegacyTrackingEvent(event, timestamp);

      if (isGTMEnabled) {
        pushToGTMDataLayer(trackingEvent);
      } else {
        // biome-ignore lint/suspicious/noConsole: allow console logging in dev
        console.log('Legacy GTM Tracking Event (dev):', trackingEvent);
      }

      // Send to LogStash
      addToQueue(trackingEvent.data);
    }
  }, [authResolved, createLegacyTrackingEvent, isGTMEnabled, addToQueue]);

  const track = useCallback(
    (event: { event: string; data?: any }) => {
      // Always log in development for debugging
      if (!isGTMEnabled) {
        const devEvent = createLegacyTrackingEvent(event);
        // biome-ignore lint/suspicious/noConsole: allow console logging in dev
        console.log('Legacy GTM Tracking Event (dev):', devEvent);
        return;
      }

      // If auth not resolved yet, queue the event
      if (!authResolved) {
        pendingEventsRef.current.push({
          event,
          timestamp: Date.now(),
        });
        return;
      }

      // Auth is resolved, send to GTM immediately
      const trackingEvent = createLegacyTrackingEvent(event);
      pushToGTMDataLayer(trackingEvent);

      // Send to LogStash
      addToQueue(trackingEvent.data);
    },
    [createLegacyTrackingEvent, isGTMEnabled, authResolved, addToQueue],
  );

  const contextValue: LegacyTrackingContextProps = {
    track,
    flushEvents, // No-op for GTM, but keeps API compatibility
    isLoading: false, // GTM is always "ready"
    error: null, // GTM doesn't have traditional errors
    queueSize: pendingEventsRef.current.length, // Only pending auth-resolution events
  };

  return (
    <LegacyTrackingContext.Provider value={contextValue}>{children}</LegacyTrackingContext.Provider>
  );
};

export const useLegacyTracking = (): LegacyTrackingContextProps => {
  const context = useContext(LegacyTrackingContext);
  if (!context) {
    throw new Error('useLegacyTracking must be used within a LegacyTrackingProvider');
  }
  return context;
};
